import { Injectable } from '@angular/core';
import { HistoryItemResponse } from '@shared/index';
import { dialogRecorderGetDialogSchema, dialogRecorderIdSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { OpenaiAdminService } from './shared/services/ai/text/openai-admin.service';
import { EventManagerAdminService } from './shared/services/event-manager/event-manager-admin.service';
import { KeyValAdminService } from './shared/services/key-val-admin.service';
import { MessageManagerAdminService } from './shared/services/message-manager-admin.service';

const KEYVAL_TYPE = 'DIALOGRECORDER';

@Injectable({ providedIn: 'root' })
export class DialogRecorderService {
  private _recorders = new Map<string, RecorderState>();

  /** @deprecated Nur für Abwärtskompatibilität mit alten Blöcken */
  public BOT_NAME = 'Bot';
  /** @deprecated Nur für Abwärtskompatibilität mit alten Blöcken */
  public USER_NAME = 'User';

  constructor(
    private MSG: MessageManagerAdminService,
    private AITEXT_OPENAI: OpenaiAdminService,
    private EVENTS: EventManagerAdminService,
    private KEYVAL: KeyValAdminService
  ) { }

  private _getOrCreate(id: string): RecorderState {
    if (!this._recorders.has(id)) {
      this._recorders.set(id, {
        isRecording: false,
        messages: [],
        hookIdPrompt: '',
        hookIdRunComplete: ''
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._recorders.get(id)!;
  }

  /** Legt einen neuen Recorder an, ohne die Aufzeichnung zu starten. */
  public async init(id: string | z.input<typeof dialogRecorderIdSchema>) {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._getOrCreate(id as string);
    // Nach einem Seiten-Reload gespeicherte Messages aus KEYVAL wiederherstellen
    if (state.messages.length === 0) {
      const stored = await this.KEYVAL.get('dialogrecorder_' + (id as string), KEYVAL_TYPE) as SimpleDialogMessage[] | null;
      if (Array.isArray(stored) && stored.length > 0) {
        state.messages = stored;
      }
    }
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.INITIALIZED', { id });
  }

  public record(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._getOrCreate(id as string);
    if (state.isRecording) return;
    state.isRecording = true;

    const idStr = id as string;
    state.hookIdPrompt = this.EVENTS.on('RAGAI.MSG.prompt', (_eventName: string, ...args: unknown[]) => {
      const promptPayload = this._getPromptPayload(args[0]);
      if (promptPayload?.pInput) {
        const text = promptPayload.pInput;
        state.messages.push({ role: 'user', text });
        void this.KEYVAL.set('dialogrecorder_' + idStr, state.messages, KEYVAL_TYPE);
        this.EVENTS.trigger('RAGAI.DIALOGRECORDER.RECORDED', { id: idStr, role: 'user', text });
      }
    });

    state.hookIdRunComplete = this.EVENTS.on('RAGAI.MSG.runComplete', (_eventName: string, ...args: unknown[]) => {
      const entry: HistoryItemResponse = args[0] as HistoryItemResponse;
      const content = entry.content;
      if (entry.bypass || entry.role !== 'assistant') return;
      if (content[0]?.type === 'text' && content[0].text.trim() !== '') {
        const text = content[0].text;
        state.messages.push({ role: 'bot', text });
        void this.KEYVAL.set('dialogrecorder_' + idStr, state.messages, KEYVAL_TYPE);
        this.EVENTS.trigger('RAGAI.DIALOGRECORDER.RECORDED', { id: idStr, role: 'bot', text });
      }
    });
  }

  public async stop(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    const state = this._recorders.get(id as string);
    if (!state) return;
    state.isRecording = false;
    this.EVENTS.off(state.hookIdPrompt);
    this.EVENTS.off(state.hookIdRunComplete);
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.STOPPED', { id, messages: state.messages });
    await this.KEYVAL.set('dialogrecorder_' + (id as string), state.messages, KEYVAL_TYPE);
  }

  public async reset(id: string | z.input<typeof dialogRecorderIdSchema> = 'default') {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    await this.stop(id);
    const state = this._recorders.get(id as string);
    if (state) state.messages = [];
    this.EVENTS.trigger('RAGAI.DIALOGRECORDER.RESET', { id });
    await this.KEYVAL.set('dialogrecorder_' + (id as string), [], KEYVAL_TYPE);
  }

  private async _getMessages(id: string): Promise<SimpleDialogMessage[]> {
    if (this._recorders.has(id)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this._recorders.get(id)!.messages;
    }
    // Nach Seiten-Reload aus KEYVAL wiederherstellen
    const stored = await this.KEYVAL.get('dialogrecorder_' + id, KEYVAL_TYPE) as SimpleDialogMessage[] | null;
    if (Array.isArray(stored) && stored.length > 0) {
      this._getOrCreate(id).messages = stored;
      return stored;
    }
    return [];
  }

  public async getDialogText(id: string | z.input<typeof dialogRecorderGetDialogSchema> = 'default', userName = 'User', botName = 'Bot'): Promise<string> {
    const parsed = dialogRecorderGetDialogSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      userName = parsed.data.userName ?? userName;
      botName = parsed.data.botName ?? botName;
      id = parsed.data.recorderId;
    }
    const messages = await this._getMessages(id as string);
    return messages.map(m =>
      m.role === 'user' ? `${userName}: ${m.text}` : `${botName}: ${m.text}`
    ).join('\n');
  }

  public async getDialogHtml(id: string | z.input<typeof dialogRecorderGetDialogSchema> = 'default', userName = 'User', botName = 'Bot'): Promise<string> {
    const parsed = dialogRecorderGetDialogSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      userName = parsed.data.userName ?? userName;
      botName = parsed.data.botName ?? botName;
      id = parsed.data.recorderId;
    }
    return (await this.getDialogText(id, userName, botName)).replace(/\n/g, '<br />');
  }

  public async getDialogJson(id: string | z.input<typeof dialogRecorderIdSchema> = 'default'): Promise<SimpleDialogMessage[]> {
    const parsed = dialogRecorderIdSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') { id = parsed.data.recorderId; }
    return this._getMessages(id as string);
  }

  /** @deprecated Verwende getDialogText(id, userName, botName) */
  public async getDialog(): Promise<string> {
    return this.getDialogText('default', this.USER_NAME, this.BOT_NAME);
  }

  private _getPromptPayload(payload: unknown): { pInput?: string } | null {
    if (!payload || typeof payload !== 'object') return null;
    return payload;
  }
}

type RecorderState = {
  isRecording: boolean;
  messages: SimpleDialogMessage[];
  hookIdPrompt: string;
  hookIdRunComplete: string;
};

export type SimpleDialogMessage = {
  role: 'user' | 'bot';
  text: string;
};
